import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import './myJoyNetFeed.css';
import ShareModal from '../ShareModal/ShareModal';
import { createPopup } from '../../utils/popupUtils';
import { UserContext } from '../../context/UserContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import PostCard from '../SharedFeed/PostCard';
import CommentsSidebar from '../SharedFeed/CommentsSidebar';
import * as feedApi from '../../utils/feedApi';

// Helper to check if a post has media to display
const hasDirectMedia = (post) => {
    const looksLikeUrl = (str) => typeof str === 'string' && str.match(/^https?:\/\//i);
    return looksLikeUrl(post.url) ||
           (post.source === 'tiktok' && post.id) ||
           (post.source === 'youtube' && (post.video_id || post.v_id || post.id));
};

// Players are mounted only for posts in [active - BEHIND, active + AHEAD].
// Posts ahead are preloaded so a gesture can unlock them before they're reached.
const MOUNT_BEHIND = 1;
const MOUNT_AHEAD = 3;

// Touch paging on the gesture layer
const TAP_SLOP_PX = 10;
const SWIPE_DISTANCE_RATIO = 0.18;
const SWIPE_VELOCITY_PX_PER_MS = 0.35;
const SNAP_RESTORE_MS = 450;

// Sound preference for this browser session: 'on', 'off', or null (not chosen yet)
const SOUND_PREF_KEY = 'joynet-sound-on';
const readSoundPref = () => {
    try {
        const value = sessionStorage.getItem(SOUND_PREF_KEY);
        return value === 'on' || value === 'off' ? value : null;
    } catch {
        return null;
    }
};
const writeSoundPref = (value) => {
    try {
        sessionStorage.setItem(SOUND_PREF_KEY, value);
    } catch {
        // Storage unavailable (private mode); the preference just won't persist.
    }
};

const EMPTY_SOUND_STATUS = { postId: null, ready: false, playing: null, muted: null, blocked: false };

// Sound is wanted but the player isn't audible (blocked, or it muted itself).
const needsSoundRetry = (player) => {
    if (!player) return false;
    const status = player.getStatus();
    return status.blocked || status.muted === true;
};

const MyJoynetFeed = ({ initialPostId }) => {
    const { user } = useContext(UserContext);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [videoStats, setVideoStats] = useState({});
    const [likedVideos, setLikedVideos] = useState(new Set());
    const [hasMore, setHasMore] = useState(true);
    const [nextCursor, setNextCursor] = useState(null);
    const [, setFeedSeed] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [newComment, setNewComment] = useState("");
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [activePostId, setActivePostId] = useState(null);
    const containerRef = useRef();
    const scrollContainerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const [currentPostIndex, setCurrentPostIndex] = useState(0);
    const postRefs = useRef([]);
    const loadMoreRef = useRef(null); // For infinite scroll observer
    const [autoplayEnabled] = useState(() => {
        const saved = localStorage.getItem('joynet-autoplay-enabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    
    // Playback system: one controller per mounted player (see SharedFeed/embedPlayers.js).
    // Every play/pause/mute decision goes through applyPlayback so it can run
    // synchronously inside user gestures (that is what unlocks sound on iOS).
    const [activeVideoId, setActiveVideoId] = useState(null);
    const playersRef = useRef(new Map());
    const videoObserverRef = useRef(null);
    const interactionCooldownRef = useRef(false);
    const programmaticScrollRef = useRef(false);
    const draggingRef = useRef(false);
    const snapRestoreTimerRef = useRef(null);
    const userPausedRef = useRef(false);
    const playCountedVideos = useRef(new Set()); // Track which videos have been counted for plays

    // Sound: the user's session preference plus what the active player
    // actually reports, so the UI never claims sound that isn't playing.
    const [soundPref, setSoundPref] = useState(readSoundPref);
    const soundOn = soundPref === 'on';
    const soundOnRef = useRef(soundOn);
    const [activeSound, setActiveSound] = useState(EMPTY_SOUND_STATUS);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const desiredFor = (postId) => (
        postId === activeVideoIdRef.current
            ? { playing: !userPausedRef.current, muted: !soundOnRef.current }
            : { playing: false, muted: true }
    );

    // Pause/mute every other player, then drive the active one.
    const applyPlayback = useCallback((activeId) => {
        playersRef.current.forEach((player, id) => {
            if (id !== activeId) player.setDesired({ playing: false, muted: true });
        });
        const active = activeId ? playersRef.current.get(activeId) : null;
        if (active) {
            active.setDesired({ playing: !userPausedRef.current, muted: !soundOnRef.current });
        }
    }, []);

    // Must run inside a user gesture: silently unlocks every mounted player so
    // later unmutes work without one, and retries sound on a blocked active video.
    const unlockAhead = useCallback(() => {
        const activeId = activeVideoIdRef.current;
        playersRef.current.forEach((player, id) => {
            if (id === activeId) {
                if (soundOnRef.current && needsSoundRetry(player)) player.retrySound();
                return;
            }
            player.unlock();
        });
    }, []);

    const registerPlayer = useCallback((postId, player) => {
        if (player) {
            playersRef.current.set(postId, player);
            player.setDesired(desiredFor(postId));
        } else {
            playersRef.current.delete(postId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSoundStatus = useCallback((postId, status) => {
        if (postId !== activeVideoIdRef.current) return;
        setActiveSound(prev => (
            prev.postId === postId &&
            prev.ready === status.ready &&
            prev.playing === status.playing &&
            prev.muted === status.muted &&
            prev.blocked === status.blocked
        ) ? prev : { postId, ...status });
    }, []);

    // Make a post active and start it. Safe to call from gestures (commands go
    // out synchronously) and from the IntersectionObserver.
    const activatePost = useCallback((postId) => {
        if (activeVideoIdRef.current !== postId) {
            userPausedRef.current = false;
            activeVideoIdRef.current = postId;
            setActiveVideoId(postId);
            setIsVideoPlaying(true);
            const player = playersRef.current.get(postId);
            setActiveSound(player ? { postId, ...player.getStatus() } : { ...EMPTY_SOUND_STATUS, postId });
        }
        applyPlayback(postId);
    }, [applyPlayback]);

    const setSound = useCallback((on) => {
        const value = on ? 'on' : 'off';
        soundOnRef.current = on;
        setSoundPref(value);
        writeSoundPref(value);
        applyPlayback(activeVideoIdRef.current);
    }, [applyPlayback]);

    // Sound button / "Tap for sound" pill (runs inside the click gesture).
    // If sound is on but the video isn't audible, a tap retries it instead of
    // flipping the preference off, so the user never has to toggle twice.
    const handleToggleMute = useCallback(() => {
        const activeId = activeVideoIdRef.current;
        const active = activeId ? playersRef.current.get(activeId) : null;
        if (soundOnRef.current && needsSoundRetry(active)) {
            active.retrySound();
            return;
        }
        setSound(!soundOnRef.current);
    }, [setSound]);

    const handleEnableSound = useCallback(() => {
        const activeId = activeVideoIdRef.current;
        const active = activeId ? playersRef.current.get(activeId) : null;
        if (soundOnRef.current) {
            if (active) active.retrySound();
            return;
        }
        setSound(true);
    }, [setSound]);

    // Pause everything and clear the active post
    const pauseAllVideos = () => {
        activeVideoIdRef.current = null;
        applyPlayback(null);
        setActiveVideoId(null);
        setIsVideoPlaying(false);
    };

    const submitComment = async (postId) => {
        if (!newComment.trim() || !user) return;

        try {
            const result = await feedApi.addComment(postId, newComment.trim());
            setNewComment("");
            
            // Update stats from server response
            if (result.stats) {
                setVideoStats(prev => ({ ...prev, [postId]: result.stats }));
            }
            
            // Use server-returned comments
            if (result.comments) {
                setComments(result.comments);
            }
        } catch (error) {
            console.error("Error posting comment:", error);
        }
    };

    const loadComments = useCallback(async (postId) => {
        try {
            const loadedComments = await feedApi.getComments(postId);
            setComments(loadedComments);
        } catch (error) {
            console.error("Error loading comments:", error);
        }
    }, []);

    const toggleComments = useCallback(async (postId) => {
        if (postId === activePostId && showComments) {
            setShowComments(false);
            setActivePostId(null);
        } else {
            setShowComments(true);
            setActivePostId(postId);
            const loadedComments = await feedApi.getComments(postId);
            setComments(loadedComments);
        }
    }, [activePostId, showComments]);

    useEffect(() => {
        if (user) {
            fetchLikedVideos();
        }
    }, [user]);

    const fetchLikedVideos = async () => {
        try {
            const liked = await feedApi.getLikedVideos();
            setLikedVideos(new Set(liked));
        } catch (error) {
            console.error('Error fetching liked videos:', error);
        }
    };

    const handleLike = useCallback(async (post) => {
        if (!user) return;

        const postId = post.id;
        const wasLiked = likedVideos.has(postId);

        // Optimistic UI update
        setLikedVideos(prev => {
            const newSet = new Set(prev);
            if (wasLiked) {
                newSet.delete(postId);
            } else {
                newSet.add(postId);
            }
            return newSet;
        });

        try {
            const result = await feedApi.toggleLike(postId, post.source);
            
            // Update stats from server response
            if (result.stats) {
                setVideoStats(prev => ({ ...prev, [postId]: result.stats }));
            }
            // Sync liked videos from server
            if (result.likedVideos) {
                setLikedVideos(new Set(result.likedVideos));
            }
        } catch (error) {
            console.error('Error updating like status:', error);
            // Revert optimistic update
            setLikedVideos(prev => {
                const newSet = new Set(prev);
                if (wasLiked) {
                    newSet.add(postId);
                } else {
                    newSet.delete(postId);
                }
                return newSet;
            });
        }
    }, [user, likedVideos]);

    const fetchInitialPosts = async () => {
        setLoading(true);
        try {
            console.log("Fetching initial MyJoynet posts via server API...");
            
            // 1. Create a new feed session (new shuffle seed)
            const session = await feedApi.createFeedSession();
            setFeedSeed(session.seed);
            setSessionId(session.sessionId);

            // 2. Fetch first page of feed using server-driven cursor pagination
            const result = await feedApi.fetchFeed({
                seed: session.seed,
                initialPostId: initialPostId || undefined
            });

            console.log("Initial posts loaded:", result.posts.length);
            setPosts(result.posts);
            setNextCursor(result.nextCursor);
            setHasMore(result.hasMore);

            // 3. Extract stats from server response (already enriched by server)
            const newStats = {};
            result.posts.forEach(post => {
                if (post.stats) {
                    newStats[post.id] = post.stats;
                }
            });
            setVideoStats(newStats);

            // 4. Record impressions for analytics
            if (session.sessionId && result.posts.length > 0) {
                feedApi.recordImpressions(session.sessionId, result.posts.map(p => p.id))
                    .catch(err => console.error('Error recording impressions:', err));
            }
        } catch (error) {
            console.error("Error fetching posts:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMorePosts = async () => {
        if (loading || !hasMore || !nextCursor) return;

        setLoading(true);
        try {
            // Use cursor-based pagination — server handles ordering & deduplication
            const result = await feedApi.fetchFeed({ cursor: nextCursor });

            // Deduplicate: only append posts whose IDs aren't already in state
            setPosts(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newPosts = result.posts.filter(p => !existingIds.has(p.id));
                return [...prev, ...newPosts];
            });
            setNextCursor(result.nextCursor);
            setHasMore(result.hasMore);

            // Extract stats from server response
            setVideoStats(prev => {
                const newStats = { ...prev };
                result.posts.forEach(post => {
                    if (post.stats) {
                        newStats[post.id] = post.stats;
                    }
                });
                return newStats;
            });

            // Record impressions
            if (sessionId && result.posts.length > 0) {
                feedApi.recordImpressions(sessionId, result.posts.map(p => p.id))
                    .catch(err => console.error('Error recording impressions:', err));
            }

            console.log("Loaded more posts:", result.posts.length);
        } catch (error) {
            console.error("Error fetching more posts:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPostId]);

    // Infinite scroll observer
    useEffect(() => {
        const currentRef = loadMoreRef.current;
        if (!currentRef) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    fetchMorePosts();
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        observer.observe(currentRef);

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loading, nextCursor]);

    // Save autoplay preference to localStorage
    useEffect(() => {
        localStorage.setItem('joynet-autoplay-enabled', JSON.stringify(autoplayEnabled));
    }, [autoplayEnabled]);

    // Stable refs for callbacks used in observers (avoids recreating observers)
    const autoplayEnabledRef = React.useRef(autoplayEnabled);
    const postsRef = React.useRef(posts);
    const activeVideoIdRef = React.useRef(activeVideoId);
    const isVideoPlayingRef = React.useRef(isVideoPlaying);

    // Keep refs in sync
    useEffect(() => { autoplayEnabledRef.current = autoplayEnabled; }, [autoplayEnabled]);
    useEffect(() => { postsRef.current = posts; }, [posts]);
    useEffect(() => { activeVideoIdRef.current = activeVideoId; }, [activeVideoId]);
    useEffect(() => { isVideoPlayingRef.current = isVideoPlaying; }, [isVideoPlaying]);

    // Observer-driven activation (no gesture). Skipped while our own touch
    // paging or a programmatic scroll is in charge of the active post.
    const playVideoStable = React.useCallback((postId) => {
        if (!autoplayEnabledRef.current) return;
        if (interactionCooldownRef.current) return;
        if (draggingRef.current || programmaticScrollRef.current) return;
        if (activeVideoIdRef.current === postId) return;
        activatePost(postId);
    }, [activatePost]);

    const currentIndex = () => {
        const container = scrollContainerRef.current;
        const activeIdx = postsRef.current.findIndex(p => p.id === activeVideoIdRef.current);
        if (activeIdx >= 0) return activeIdx;
        if (!container || !container.clientHeight) return 0;
        return Math.round(container.scrollTop / container.clientHeight);
    };

    const restoreSnap = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        container.style.scrollSnapType = '';
        container.style.scrollBehavior = '';
    };

    // Page to a post. Called from gestures (swipe end, arrow buttons, keys):
    // the incoming player gets play/unMute synchronously, inside the gesture,
    // before the scroll animation starts.
    const goToIndex = useCallback((index) => {
        const container = scrollContainerRef.current;
        const list = postsRef.current;
        if (!container || list.length === 0) return;
        const clamped = Math.max(0, Math.min(index, list.length - 1));
        const target = list[clamped];
        const el = postRefs.current[clamped];

        activatePost(target.id);
        const player = playersRef.current.get(target.id);
        if (soundOnRef.current && player?.getStatus().blocked) player.retrySound();

        programmaticScrollRef.current = true;
        container.style.scrollSnapType = 'none';
        container.style.scrollBehavior = 'auto';
        const top = el
            ? el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
            : clamped * container.clientHeight;
        container.scrollTo({ top, behavior: 'smooth' });

        clearTimeout(snapRestoreTimerRef.current);
        snapRestoreTimerRef.current = setTimeout(() => {
            restoreSnap();
            programmaticScrollRef.current = false;
        }, SNAP_RESTORE_MS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activatePost]);

    // Tap on the gesture layer: retry blocked sound, otherwise play/pause.
    const handleMediaTap = useCallback(() => {
        const activeId = activeVideoIdRef.current;
        if (!activeId) return;
        const player = playersRef.current.get(activeId);
        if (soundOnRef.current && player?.getStatus().blocked) {
            player.retrySound();
            return;
        }
        handleTogglePlayPause(activeId, userPausedRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Any user gesture anywhere on the page unlocks the mounted players ahead.
    // Capture phase, so it runs before (and regardless of) other handlers.
    useEffect(() => {
        const onGesture = (e) => {
            if (e.type === 'keydown' && (e.key === 'Escape' || e.metaKey || e.ctrlKey || e.altKey)) return;
            unlockAhead();
        };
        const types = ['touchend', 'click', 'keydown'];
        types.forEach(t => document.addEventListener(t, onGesture, { capture: true, passive: true }));
        return () => types.forEach(t => document.removeEventListener(t, onGesture, { capture: true }));
    }, [unlockAhead]);

    // Touch paging on the gesture layer. The page handles the swipe itself
    // (instead of native scrolling) so the touchend is a real user gesture
    // that can start the next video with sound.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        let start = null;

        const onTouchStart = (e) => {
            if (e.touches.length !== 1 || !e.target.closest('[data-gesture-layer]')) {
                start = null;
                return;
            }
            start = {
                y: e.touches[0].clientY,
                top: container.scrollTop,
                t: performance.now(),
                index: currentIndex(),
                moved: false,
            };
            draggingRef.current = true;
            clearTimeout(snapRestoreTimerRef.current);
            container.style.scrollSnapType = 'none';
            container.style.scrollBehavior = 'auto';
        };

        const onTouchMove = (e) => {
            if (!start) return;
            e.preventDefault();
            const dy = start.y - e.touches[0].clientY;
            if (Math.abs(dy) > TAP_SLOP_PX) start.moved = true;
            if (start.moved) container.scrollTop = start.top + dy;
        };

        const onTouchEnd = (e) => {
            if (!start) return;
            const s = start;
            start = null;
            draggingRef.current = false;
            if (!s.moved) {
                restoreSnap();
                // Suppress the synthetic click; the tap is handled here.
                e.preventDefault();
                handleMediaTap();
                return;
            }
            const dy = s.y - e.changedTouches[0].clientY;
            const velocity = dy / Math.max(1, performance.now() - s.t);
            const threshold = container.clientHeight * SWIPE_DISTANCE_RATIO;
            let target = s.index;
            if (dy > threshold || velocity > SWIPE_VELOCITY_PX_PER_MS) target = s.index + 1;
            else if (dy < -threshold || velocity < -SWIPE_VELOCITY_PX_PER_MS) target = s.index - 1;
            goToIndex(target);
        };

        const onTouchCancel = () => {
            if (!start) return;
            const s = start;
            start = null;
            draggingRef.current = false;
            goToIndex(s.index);
        };

        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false });
        container.addEventListener('touchend', onTouchEnd, { passive: false });
        container.addEventListener('touchcancel', onTouchCancel, { passive: true });
        return () => {
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
            container.removeEventListener('touchcancel', onTouchCancel);
            clearTimeout(snapRestoreTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goToIndex, handleMediaTap]);

    // Keyboard paging (key presses are user gestures too)
    useEffect(() => {
        const onKeyDown = (e) => {
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === 'j') {
                e.preventDefault();
                goToIndex(currentIndex() + 1);
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'k') {
                e.preventDefault();
                goToIndex(currentIndex() - 1);
            } else if (e.key === 'm') {
                handleToggleMute();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goToIndex, handleToggleMute]);

    // Cumulative visibility map — tracks ratio for every observed element,
    // not just the entries that changed in a given callback.
    const visibilityMapRef = useRef(new Map());

    // IntersectionObserver rooted to the scroll container so visibility
    // is tracked relative to .posts-container, not the viewport. Recreated
    // once when the scroll container ref is available.
    useEffect(() => {
        const root = scrollContainerRef.current;
        if (!root) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Always record ratios so the map never goes stale, even while
                // a gesture or programmatic scroll owns the active post.
                entries.forEach(entry => {
                    const postId = entry.target.dataset.postId;
                    if (postId) {
                        visibilityMapRef.current.set(postId, entry.intersectionRatio);
                    }
                });

                if (interactionCooldownRef.current || draggingRef.current || programmaticScrollRef.current) return;

                let mostVisible = null;
                let maxRatio = 0;
                visibilityMapRef.current.forEach((ratio, id) => {
                    if (ratio > maxRatio) {
                        maxRatio = ratio;
                        mostVisible = id;
                    }
                });

                if (mostVisible && maxRatio >= 0.5) {
                    playVideoStable(mostVisible);
                } else if (maxRatio < 0.5 && activeVideoIdRef.current) {
                    pauseAllVideos();
                }
            },
            {
                root: root,
                threshold: [0, 0.25, 0.5, 0.75],
                rootMargin: '0px'
            }
        );

        videoObserverRef.current = observer;
        const visMap = visibilityMapRef.current;

        return () => {
            observer.disconnect();
            visMap.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Observe new posts when they're added (without recreating observer)
    useEffect(() => {
        const observer = videoObserverRef.current;
        if (!observer) return;

        const containers = scrollContainerRef.current?.querySelectorAll('.post-snap-container[data-post-id]');
        if (containers) {
            containers.forEach(container => {
                observer.observe(container);
            });
        }
    }, [posts]);

    // Lightweight scroll listener — ONLY updates currentPostIndex for the
    // comments sidebar. Video play/pause is handled exclusively by the
    // IntersectionObserver above (single source of truth, no races).
    useEffect(() => {
        const handleScroll = () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => detectCurrentPost(), 150);
        };

        const postsContainer = scrollContainerRef.current;
        if (postsContainer) {
            postsContainer.addEventListener('scroll', handleScroll, { passive: true });
        }
        return () => {
            if (postsContainer) postsContainer.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clean up on unmount
    useEffect(() => {
        const players = playersRef.current;
        return () => {
            players.forEach(player => player.setDesired({ playing: false, muted: true }));
            players.clear();
        };
    }, []);

    // Play first video on initial load
    useEffect(() => {
        if (posts.length > 0 && !loading && autoplayEnabled && !activeVideoId) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                const firstPost = posts.find(p => hasDirectMedia(p));
                if (firstPost) {
                    playVideoStable(firstPost.id);
                }
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posts.length, loading, autoplayEnabled, activeVideoId, playVideoStable]);

    const handleShareClick = useCallback((post) => {
        setSelectedPost(post);
        setIsShareModalOpen(true);
    }, []);

    const handleCloseShareModal = useCallback(() => {
        setIsShareModalOpen(false);
        setSelectedPost(null);
    }, []);

    const handleShare = useCallback(async (post) => {
        try {
            // Optimistic UI update
            setVideoStats(prev => {
                const newStats = { ...prev };
                if (!newStats[post.id]) {
                    newStats[post.id] = { total_likes: 0, total_shares: 0, total_plays: 0, total_comments: 0 };
                }
                newStats[post.id].total_shares = (newStats[post.id].total_shares || 0) + 1;
                return newStats;
            });

            // Server handles the stat update
            const result = await feedApi.recordShare(post.id);
            if (result.stats) {
                setVideoStats(prev => ({ ...prev, [post.id]: result.stats }));
            }
        } catch (error) {
            console.error('Error updating share count:', error);
            // Revert optimistic update
            setVideoStats(prev => {
                const newStats = { ...prev };
                if (!newStats[post.id]) return prev;
                newStats[post.id].total_shares = Math.max(0, (newStats[post.id].total_shares || 0) - 1);
                return newStats;
            });
        }
    }, []);

    const handleReportContent = useCallback(async (post) => {
        if (!user) {
            createPopup("You must be logged in to report content.", "error");
            return;
        }

        const confirmed = window.confirm("Are you sure you want to report this content?");
        if (!confirmed) return;

        try {
            await feedApi.reportContent(post.id, post.source);
            createPopup("Content reported. Thank you for helping keep the platform safe.", "success");
        } catch (error) {
            console.error("Error reporting content:", error);
            createPopup("Failed to report content. Please try again.", "error");
        }
    }, [user]);

    const handlePlay = useCallback(async (post) => {
        // Only count each video once per session
        if (playCountedVideos.current.has(post.id)) {
            return;
        }
        
        playCountedVideos.current.add(post.id);
        
        // Optimistic UI update
        setVideoStats(prev => {
            const newStats = { ...prev };
            if (!newStats[post.id]) {
                newStats[post.id] = { total_likes: 0, total_shares: 0, total_plays: 0, total_comments: 0 };
            }
            newStats[post.id].total_plays = (newStats[post.id].total_plays || 0) + 1;
            return newStats;
        });

        // Server handles the stat update
        try {
            const result = await feedApi.recordPlay(post.id);
            if (result.stats) {
                setVideoStats(prev => ({ ...prev, [post.id]: result.stats }));
            }
        } catch (error) {
            console.error('Error recording play:', error);
        }
    }, []);

    // Play/pause button or tap (runs inside the gesture). Sound is untouched.
    const handleTogglePlayPause = useCallback((postId, isNowPlaying) => {
        interactionCooldownRef.current = true;
        setTimeout(() => { interactionCooldownRef.current = false; }, 500);

        if (activeVideoIdRef.current !== postId) activatePost(postId);
        userPausedRef.current = !isNowPlaying;
        setIsVideoPlaying(isNowPlaying);
        applyPlayback(postId);
    }, [activatePost, applyPlayback]);

    // Detect which post is currently in view
    const detectCurrentPost = () => {
        const postsContainer = scrollContainerRef.current;
        if (!postsContainer || postRefs.current.length === 0) return;

        const containerRect = postsContainer.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        let closestIndex = 0;
        let closestDistance = Infinity;

        postRefs.current.forEach((ref, index) => {
            if (ref) {
                const rect = ref.getBoundingClientRect();
                const postCenter = rect.top + rect.height / 2;
                const distance = Math.abs(postCenter - containerCenter);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            }
        });

        if (closestIndex !== currentPostIndex) {
            setCurrentPostIndex(closestIndex);
            
            // Update comments if sidebar is open
            const newPostId = posts[closestIndex]?.id;
            if (showComments && newPostId && newPostId !== activePostId) {
                setActivePostId(newPostId);
                loadComments(newPostId);
            }
        }
    };

    // Update comments when post changes and sidebar is open
    useEffect(() => {
        if (posts.length > 0 && currentPostIndex < posts.length) {
            const currentPost = posts[currentPostIndex];
            if (showComments && currentPost && currentPost.id !== activePostId) {
                setActivePostId(currentPost.id);
                loadComments(currentPost.id);
            }
        }
    }, [currentPostIndex, posts, showComments, activePostId, loadComments]);

    const activeIndex = Math.max(0, posts.findIndex(p => p.id === activeVideoId));

    return (
        <>
            <div className="feed-container" ref={containerRef}>
                <div className="feed-main">
                    <div className="feed-nav" aria-label="Feed navigation">
                        <button
                            className="feed-nav-btn"
                            aria-label="Previous video"
                            disabled={activeIndex === 0}
                            onClick={() => goToIndex(currentIndex() - 1)}
                        >
                            <FiChevronUp />
                        </button>
                        <button
                            className="feed-nav-btn"
                            aria-label="Next video"
                            disabled={activeIndex >= posts.length - 1}
                            onClick={() => goToIndex(currentIndex() + 1)}
                        >
                            <FiChevronDown />
                        </button>
                    </div>
                    <div className="posts-container" ref={scrollContainerRef}>
                        {posts.length === 0 && loading ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                {posts.map((post, index) => {
                                    const isActive = activeVideoId === post.id;
                                    return (
                                        <div 
                                            key={post.id} 
                                            className="post-snap-container"
                                            data-post-id={post.id}
                                            ref={el => postRefs.current[index] = el}
                                        >
                                            <PostCard
                                                post={post}
                                                videoStats={videoStats}
                                                likedVideos={likedVideos}
                                                registerPlayer={registerPlayer}
                                                onSoundStatus={handleSoundStatus}
                                                isActive={isActive}
                                                shouldMountPlayer={index >= activeIndex - MOUNT_BEHIND && index <= activeIndex + MOUNT_AHEAD}
                                                isPlaying={isActive && isVideoPlaying}
                                                soundPref={soundPref}
                                                soundStatus={isActive && activeSound.postId === post.id ? activeSound : null}
                                                onToggleMute={handleToggleMute}
                                                onEnableSound={handleEnableSound}
                                                onMediaTap={handleMediaTap}
                                                onLike={handleLike}
                                                onShare={handleShareClick}
                                                onComment={toggleComments}
                                                onReport={handleReportContent}
                                                onPlay={handlePlay}
                                                onTogglePlayPause={handleTogglePlayPause}
                                            />
                                        </div>
                                    );
                                })}
                                
                                {/* Infinite scroll trigger */}
                                <div ref={loadMoreRef} className="load-more-trigger">
                                    {loading && posts.length > 0 && (
                                        <div className="loading-more">
                                            <LoadingSpinner />
                                        </div>
                                    )}
                                    {!hasMore && posts.length > 0 && (
                                        <div className="end-of-feed">
                                            No more posts to load
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <CommentsSidebar
                showComments={showComments}
                comments={comments}
                newComment={newComment}
                setNewComment={setNewComment}
                onSubmit={() => submitComment(activePostId)}
                onClose={() => setShowComments(false)}
                description={(() => {
                    const p = posts.find(p => p.id === activePostId);
                    return p?.description || p?.caption || p?.text || '';
                })()}
                postUsername={(() => {
                    const p = posts.find(p => p.id === activePostId);
                    return p?.username || p?.author || 'username';
                })()}
            />

            <ShareModal
                isOpen={isShareModalOpen}
                onClose={handleCloseShareModal}
                post={selectedPost}
                onShare={() => selectedPost && handleShare(selectedPost)}
            />
        </>
    );
};

export default MyJoynetFeed;
